import { ReactNode } from "react";
import DashboardSidebar from "@/components/dashboard/DashboardSidebar";
import { cn } from "@/lib/utils";

/**
 * PageShell — premium page wrapper matching the new dashboard facelift.
 * Provides the dark navy background, subtle radial accents, and the
 * shared max-w-5xl container with mobile-safe padding.
 */
interface PageShellProps {
  children: ReactNode;
  className?: string;
  /** Render without the inner max-width container (rare). */
  bare?: boolean;
  /** Hide the persistent sidebar + utility bar (use only for fullscreen flows). */
  noChrome?: boolean;
  /**
   * Use the wider intelligence-console max width (used by the Parent Hub).
   * Defaults to the site-wide max-w-5xl.
   */
  wide?: boolean;
}

export default function PageShell({
  children,
  className,
  bare,
  noChrome,
  wide,
}: PageShellProps) {
  const content = bare ? (
    <div className={cn("relative", className)}>{children}</div>
  ) : (
    <main
      className={cn(
        "relative mx-auto w-full px-4 py-6 sm:px-6 lg:px-8 lg:py-8 space-y-5 lg:space-y-6 pt-16 lg:pt-8",
        wide ? "max-w-[1240px]" : "max-w-5xl",
        className,
      )}
    >
      {children}
    </main>
  );

  if (noChrome) {
    return (
      <div className="relative min-h-screen w-full overflow-x-hidden bg-background">
        <Ambient />
        {content}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full bg-background flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0 relative overflow-x-hidden">
        <Ambient />
        {content}
      </div>
    </div>
  );
}

function Ambient() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/4 h-72 w-72 rounded-full bg-primary/10 blur-[120px]" />
      <div className="absolute top-1/3 -right-32 h-80 w-80 rounded-full bg-secondary/10 blur-[140px]" />
      <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-accent/5 blur-[120px]" />
    </div>
  );
}